// Domain types shared between apps/api, apps/worker and packages/{checks,score,db}.
// Mirrors architecture.md §3 (API contract) and §5.1 (DB schema).

export type ScanUrlRole = "home" | "article" | "category" | "product" | "other";
export type ScanUrlSource = "user" | "auto";

export type ScanStatus = "queued" | "running" | "partial" | "complete" | "failed";

export type ScanUrlStatus =
  | "queued"
  | "running"
  | "done"
  | "timeout"
  | "bot_blocked"
  | "invalid_url"
  | "no_ads_found"
  | "cached";

export type ScanUrlErrorCode = "DNS_ERROR" | "HTTP_4XX" | "HTTP_5XX" | "HARD_TIMEOUT" | null;

export type ConsentCmp =
  | "borlabs"
  | "complianz"
  | "real_cookie_banner"
  | "cookieyes"
  | "generic"
  | "none_detected";

export interface ConsentClickResult {
  cmp: ConsentCmp;
  clicked: boolean;
  clickedAtMs: number | null;
  error?: string;
}

export type FindingCategory = "consent" | "placement" | "behavior" | "setup";
export type FindingSeverity = "critical" | "high" | "medium" | "low" | "info";
export type FindingConfidence = "high" | "medium" | "low";
export type FindingLabel = "Verstoß" | "auffällig" | "Hinweis";

export type RuleSource =
  | "adsense_placement"
  | "adsense_program"
  | "publisher_policies"
  | "gdpr"
  | "best_practice";

export type Viewport = "mobile" | "desktop";

export interface Finding {
  findingId: string;
  urlId: string | null; // null for site-wide/aggregate findings
  viewport: Viewport | null;
  ruleId: string;
  ruleVersion: string;
  category: FindingCategory;
  severity: FindingSeverity;
  source: RuleSource;
  sourceUrl: string | null;
  isOfficialViolation: boolean;
  confidence: FindingConfidence;
  label: FindingLabel;
  title: string;
  explanation: string;
  fix: string;
  adpressoSolves: boolean;
  rawValue: Record<string, unknown> | null;
  screenshotUrl: string | null;
  createdAt: string;
}

/** One occurrence of a FindingGroup's rule — the only fields that legitimately vary per instance. */
export interface FindingGroupInstance {
  rawValue: Record<string, unknown> | null;
  screenshotUrl: string | null;
}

/**
 * A rule's findings on one page (or site-wide), collapsed into one entry. Real findings that
 * share a `ruleId` always carry identical rule-derived fields (severity, confidence, title,
 * explanation, fix, ...) — those come from rule metadata resolved once at scan time — so
 * collapsing loses nothing except the (currently UI-buried) per-instance `rawValue`/
 * `screenshotUrl`, which move into `instances`.
 */
export interface FindingGroup {
  ruleId: string;
  urlId: string | null; // null for site-wide/aggregate findings
  category: FindingCategory;
  severity: FindingSeverity;
  source: RuleSource;
  sourceUrl: string | null;
  isOfficialViolation: boolean;
  confidence: FindingConfidence;
  label: FindingLabel;
  title: string;
  explanation: string;
  fix: string;
  adpressoSolves: boolean;
  instanceCount: number;
  instances: FindingGroupInstance[];
}

/**
 * Collapses repeated findings of the same rule into one group per (ruleId, urlId) — e.g. 18
 * separate "invisible ad unit" findings on one page become one group with instanceCount: 18,
 * instead of 18 near-identical report cards. Grouped by (ruleId, urlId), not ruleId alone: a
 * site owner reasons in pages ("this page has a problem"), so rolling findings up across every
 * scanned URL would obscure which page to actually go fix. Consumed by every findings-rendering
 * surface (embed-widget's reportRenderer, apps/api's PDF generator) so they can't drift from
 * each other on how grouping works.
 */
export function groupFindings(findings: Finding[]): FindingGroup[] {
  const groups = new Map<string, FindingGroup>();
  const order: string[] = [];

  for (const finding of findings) {
    const key = `${finding.ruleId}::${finding.urlId ?? "sitewide"}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        ruleId: finding.ruleId,
        urlId: finding.urlId,
        category: finding.category,
        severity: finding.severity,
        source: finding.source,
        sourceUrl: finding.sourceUrl,
        isOfficialViolation: finding.isOfficialViolation,
        confidence: finding.confidence,
        label: finding.label,
        title: finding.title,
        explanation: finding.explanation,
        fix: finding.fix,
        adpressoSolves: finding.adpressoSolves,
        instanceCount: 0,
        instances: [],
      };
      groups.set(key, group);
      order.push(key);
    }
    group.instanceCount += 1;
    group.instances.push({ rawValue: finding.rawValue, screenshotUrl: finding.screenshotUrl });
  }

  return order.map((key) => groups.get(key)!);
}

/**
 * rawValue's shape varies per rule (it's each check's own debug payload) — best-effort a short,
 * human-readable label from the shapes checks actually use, falling back to raw JSON. Shared
 * between every surface that renders a FindingGroup's instances (embed-widget's reportRenderer,
 * apps/api's PDF generator) so the two can't describe the same instance differently.
 */
export function describeFindingInstance(rawValue: Record<string, unknown> | null): string {
  if (!rawValue) return "—";
  const v = rawValue;
  if (typeof v.frame === "string") return v.frame;
  if (typeof v.adFrame === "string") return v.adFrame;
  if (typeof v.frameA === "string" && typeof v.frameB === "string") return `${v.frameA} ↔ ${v.frameB}`;
  if (typeof v.clickableElement === "string") return v.clickableElement;
  try {
    return JSON.stringify(v);
  } catch {
    return "—";
  }
}

export interface ScanRequestUrl {
  url: string;
  role: ScanUrlRole;
  source: ScanUrlSource;
}

export interface ScanRequestEmbed {
  originTag: string;
  context: "gutenberg" | "script-embed";
  hostUrl: string;
}

export interface ScanRequest {
  urls: ScanRequestUrl[];
  consentCheckbox: boolean;
  turnstileToken: string;
  embed: ScanRequestEmbed;
  emailOptIn: string | null;
}

export interface ScanCreatedUrl {
  urlId: string;
  url: string;
  role: ScanUrlRole;
  source: ScanUrlSource;
  status: ScanUrlStatus;
  cached: boolean;
  cachedAt?: string;
}

export interface ScanCreatedResponse {
  scanId: string;
  domain: string;
  ruleVersion: string;
  urls: ScanCreatedUrl[];
  pollUrl: string;
}

export interface ScanUrlStatusEntry {
  urlId: string;
  status: ScanUrlStatus;
  progressStep?: string;
  cached: boolean;
  partial?: boolean;
  errorCode?: ScanUrlErrorCode;
  score?: number | null;
}

export interface ScanEvent {
  seq: number;
  urlId: string | null;
  type: "finding" | "status";
  payload: Finding | { progressStep: string };
}

export interface ScanScoreSummary {
  domainScore: number;
  weakestUrlId: string;
  officialViolationCount: number;
  recommendationCount: number;
}

export interface ScanStatusResponse {
  scanId: string;
  domain: string;
  status: ScanStatus;
  ruleVersion: string;
  urls: ScanUrlStatusEntry[];
  events: ScanEvent[];
  score: ScanScoreSummary | null;
  nextPollMs: number;
}

export type ApiErrorCode =
  | "TURNSTILE_FAILED"
  | "DOMAIN_MISMATCH"
  | "PERMISSION_NOT_CONFIRMED"
  | "RATE_LIMIT_EXCEEDED"
  | "DOMAIN_BLOCKED"
  | "SCAN_NOT_FOUND"
  | "VALIDATION_ERROR";

export interface ApiError {
  error: {
    code: ApiErrorCode;
    message?: string;
    retryAfterSeconds?: number;
  };
}

// --- BullMQ job contract (apps/api enqueues, apps/worker consumes) ---
export const SCAN_URL_QUEUE_NAME = "scan-url";

export interface ScanUrlJobData {
  scanId: string;
  scanUrlId: string;
  url: string;
  ruleVersion: string;
}
